// Add a seek index without decoding frames or holding the whole recording in RAM.
const CLUSTER = 0x1f43b675;
function vint(bytes, offset, id = false) {
  const first = bytes[offset];
  if (!first) throw Error('Invalid WebM element');
  let length = 1, marker = 128;
  while (!(first & marker) && length < 8) {length++; marker /= 2;}
  if (offset + length > bytes.length) throw Error('Incomplete WebM element');
  let value = id ? first : first & (marker - 1);
  let unknown = !id && value === marker - 1;
  for (let i = 1; i < length; i++) {value = value * 256 + bytes[offset + i]; unknown = unknown && bytes[offset + i] === 255;}
  return {value, length, unknown};
}
function sizeBytes(value, length = 1) {
  while (value >= 2 ** (7 * length) - 1 && length < 8) length++;
  const bytes = new Uint8Array(length);
  for (let i = length - 1; i >= 0; i--) {bytes[i] = value % 256; value = Math.floor(value / 256);}
  bytes[0] |= 2 ** (8 - length);
  return bytes;
}
function uint(value) {
  const result = [];
  do {result.unshift(value % 256); value = Math.floor(value / 256);} while (value);
  return Uint8Array.from(result);
}
function join(parts) {
  const result = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {result.set(part, offset); offset += part.length;}
  return result;
}
const element = (id, content) => join([Uint8Array.from(id), sizeBytes(content.length), content]);

export default async function indexRecordedWebm(blob) {
  let cache = new Uint8Array(), cacheStart = -1;
  async function read(offset, length = 24) {
    if (offset < cacheStart || offset + length > cacheStart + cache.length) {
      cacheStart = offset;
      cache = new Uint8Array(await blob.slice(offset, offset + Math.max(65536, length)).arrayBuffer());
    }
    return cache.subarray(offset - cacheStart, offset - cacheStart + length);
  }
  async function header(offset) {
    const bytes = await read(offset);
    const id = vint(bytes, 0, true), size = vint(bytes, id.length);
    return {id: id.value, offset, start: offset + id.length + size.length,
      sizeOffset: offset + id.length, sizeLength: size.length,
      end: size.unknown ? Infinity : offset + id.length + size.length + size.value};
  }
  try {
    let segment, offset = 0;
    while (offset < blob.size) {
      const item = await header(offset);
      if (item.id === 0x18538067) {segment = item; offset = item.start; break;}
      offset = item.end;
    }
    if (!segment) return blob;
    let firstCluster, current, info, tracksHeader, emptySeekHead, videoTrack = 1;
    const clusters = [], cues = [];
    while (offset < blob.size) {
      const item = await header(offset);
      if (item.id === 0x1c53bb6b) return blob; // Already indexed.
      if (item.id === 0x114d9b74 && item.end > item.start) return blob; // Preserve existing seek offsets.
      if (item.id === 0x114d9b74) emptySeekHead = item;
      if (item.id === 0x1549a966) info = item;
      if (item.id === 0x1654ae6b && item.end - item.start < 65536) {
        tracksHeader = item;
        const tracks = await read(item.start, item.end - item.start);
        for (let p = 0; p < tracks.length;) {
          const id = vint(tracks, p, true), size = vint(tracks, p + id.length);
          const start = p + id.length + size.length, end = start + size.value;
          if (end > tracks.length || end <= p) throw Error('Invalid tracks');
          if (id.value === 0xae) {
            let number, type;
            for (let q = start; q < end;) {
              const tag = vint(tracks, q, true), length = vint(tracks, q + tag.length);
              const dataStart = q + tag.length + length.length;
              if (dataStart + length.value > end) throw Error('Invalid track');
              if (tag.value === 0xd7 || tag.value === 0x83) {
                let value = 0;
                for (let i = dataStart; i < dataStart + length.value; i++) value = value * 256 + tracks[i];
                if (tag.value === 0xd7) number = value; else type = value;
              }
              q = dataStart + length.value;
            }
            if (type === 1 && number) videoTrack = number;
          }
          p = end;
        }
      }
      if (item.id === CLUSTER) {
        if (current) current.end = offset;
        current = {...item, time: 0, indexed: false};
        clusters.push(current);
        if (!firstCluster) firstCluster = item;
        offset = item.start;
        continue;
      }
      if (current && item.id === 0xe7) {
        const bytes = await read(item.start, item.end - item.start);
        current.time = Array.from(bytes).reduce((value, byte) => value * 256 + byte, 0);
      }
      if (current && !current.indexed && item.id === 0xa3) {
        const bytes = await read(item.start, 12), track = vint(bytes, 0);
        if (track.value === videoTrack && (bytes[track.length + 2] & 0x80)) {
          const relative = new DataView(bytes.buffer, bytes.byteOffset).getInt16(track.length);
          cues.push({cluster: current, time: Math.max(0, current.time + relative), relative: item.offset - current.start});
          current.indexed = true;
        }
      }
      if (!Number.isFinite(item.end) || item.end <= offset || item.end > blob.size) return blob;
      offset = item.end;
    }
    if (!firstCluster || !cues.length) return blob;
    current.end = blob.size;
    const insertion = emptySeekHead?.offset ?? segment.start;
    const removed = emptySeekHead ? emptySeekHead.end - emptySeekHead.offset : 0;
    const directoryTargets = [info, tracksHeader, {id: 0x1c53bb6b, offset: firstCluster.offset}].filter(Boolean);
    const fixedPosition = value => {
      const result = new Uint8Array(8), encoded = uint(value);
      result.set(encoded, 8 - encoded.length);
      return result;
    };
    const directory = shift => element([0x11, 0x4d, 0x9b, 0x74], join(directoryTargets.map(target => element([0x4d, 0xbb], join([
      element([0x53, 0xab], uint(target.id)),
      element([0x53, 0xac], fixedPosition(target.offset - segment.start + shift)),
    ])))));
    const shift = directory(0).length - removed;
    const seekHead = directory(shift);
    let cueBytes = new Uint8Array();
    // Cue positions include the index itself. Iterate until integer widths settle.
    for (let i = 0; i < 8; i++) {
      const cueLength = cueBytes.length;
      const next = element([0x1c, 0x53, 0xbb, 0x6b], join(cues.map(cue => element([0xbb], join([
        element([0xb3], uint(cue.time)), element([0xb7], join([
          element([0xf7], uint(videoTrack)),
          element([0xf1], uint(cue.cluster.offset - segment.start + shift + cueLength)),
          element([0xf0], uint(cue.relative)),
        ])),
      ])))));
      if (next.length === cueBytes.length) {cueBytes = next; break;}
      cueBytes = next;
      if (i === 7) return blob;
    }
    const originalPrefix = new Uint8Array(await blob.slice(0, firstCluster.offset).arrayBuffer());
    const metadataPrefix = join([originalPrefix.subarray(0, insertion), seekHead, originalPrefix.subarray(insertion + removed)]);
    // The recording is complete: a finite Segment also lets Chromium suspend and
    // resume metadata-only preloads safely. Relative seek offsets do not change
    // if the Segment size field needs to grow.
    const prefix = join([
      metadataPrefix.subarray(0, segment.sizeOffset),
      sizeBytes(blob.size - segment.start + shift + cueBytes.length, segment.sizeLength),
      metadataPrefix.subarray(segment.sizeOffset + segment.sizeLength),
    ]);
    const parts = [prefix, cueBytes];
    for (const cluster of clusters) {
      const clusterHeader = new Uint8Array(await blob.slice(cluster.offset, cluster.start).arrayBuffer());
      clusterHeader.set(sizeBytes(cluster.end - cluster.start, cluster.sizeLength), cluster.sizeOffset - cluster.offset);
      parts.push(clusterHeader, blob.slice(cluster.start, cluster.end));
    }
    return new Blob(parts, {type: blob.type});
  } catch {
    return blob;
  }
}
